// F1 (#3988) — sign-out data-safety guard: pure decision core.
//
// Signing out deletes every local IndexedDB / SQLite database
// (reloadAppAsync clearLocalData: true). If the user has local writes that
// were never uploaded and the server is unreachable, that deletion is silent
// data loss. This module decides what the sign-out flow must do BEFORE any
// destructive step runs.
//
// This file is kept free of runtime Angular imports so it stays node-loadable
// and unit-testable the same way couch-login-online-gate.ts is (see
// sign-out-guard.test.ts). The Angular-facing orchestration that consumes this
// decision — confirmSignOutSafeAsync — lives in sign-out-guard-flow.ts, which
// imports the AlertController / OnlineStateService / DatabaseWrapper graph and
// therefore cannot be loaded under node.

export enum SignOutSafety {
  Proceed = 'Proceed',        // no unflushed changes -> sign out as today
  DrainFirst = 'DrainFirst',  // reachable + unflushed -> bounded drain, then re-decide
  WarnOffline = 'WarnOffline' // unreachable -> warning dialog, user decides
}

/** Decide what the sign-out flow must do given whether the server is reachable
 * and whether the user DB looks like it has un-uploaded local writes. Pure so
 * the four branches are unit-tested under node (see sign-out-guard.test.ts). */
export function classifySignOutSafety(input: {
  serverReachable: boolean;
  hasUnflushedChanges: boolean;
}): SignOutSafety {
  if (!input.serverReachable) {
    // if offline, we cannot trust "nothing unflushed" (a prior session may have
    // pending writes the session-scoped timestamps cannot see) -> always warn
    return SignOutSafety.WarnOffline;
  }
  return input.hasUnflushedChanges ? SignOutSafety.DrainFirst : SignOutSafety.Proceed;
}

/** Poll a "still dirty?" check until it reads clean twice in a row (the push
 * has drained) or the deadline passes. Pure: takes the dirty reader, a sleep
 * fn, and a clock so the streak-and-timeout logic is unit-testable under node
 * without a real DatabaseWrapper or real time (DatabaseWrapper.whenPushDrainedAsync
 * delegates to this). Returns true when drained, false on timeout.
 *
 * Requires TWO consecutive clean reads before concluding drained: the second
 * read catches a fresh local write landing mid-drain (it resets the streak) and
 * gives a just-flushed push one extra settle tick. It CANNOT detect the
 * inbound-pull false negative on hasUnflushedLocalChanges — both timestamps are
 * monotonic, so a clean read stays clean; that residual race is accepted there. */
export async function pollUntilDrainedAsync(deps: {
  isStillDirty: () => boolean;
  // returns Promise<unknown> so the shared-util sleep() (Promise<unknown>) wires
  // in directly; we only await it for the delay, never for its resolved value
  sleep: (ms: number) => Promise<unknown>;
  now: () => number;
  timeoutMs: number;
  pollIntervalMs?: number;
}): Promise<boolean> {
  const pollIntervalMs = deps.pollIntervalMs ?? 500;
  const deadline = deps.now() + deps.timeoutMs;
  let cleanStreak = 0;
  // loop until two clean reads in a row (drained) or the deadline passes
  while (true) {
    cleanStreak = deps.isStillDirty() ? 0 : cleanStreak + 1;
    if (cleanStreak >= 2) return true;
    if (deps.now() >= deadline) return false;
    await deps.sleep(pollIntervalMs);
  }
}
